import type React from "react";
import { useState, useMemo, useCallback } from "react";

import { chosenSites } from "app/common/services/chosenSites";
import { allTrackers } from "app/services/track/track";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { ChannelsOverviewBarChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChart";
import {
    ChartContainer,
    TotalTrafficNumber,
    TotalTrafficTitle,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/StyledComponents";

import ChannelsOverviewBarChartLegend from "../../../../components/ChannelsOverviewBarChart/components/channels-overview-bar-chart-legend/ChannelsOverviewBarChartLegend";
import type { ILegendItem, ISeries } from "../../../../components/ChannelsOverviewBarChart/types";

import { ChartWrapper, LegendsContainer, TotalSingleWrapper } from "./styledComponent";

interface IBetaChannelsChartProps {
    series: ISeries[];
    config: any;
    totalVisits: number;
    data?: Record<string, { totalVisits: number; trafficSources: any }>;
}

const BetaChannelsChart: React.FC<IBetaChannelsChartProps> = ({ series, config, totalVisits, data }) => {
    const [visibleLegends, setVisibleLegends] = useState<Set<string>>(
        () => new Set(chosenSites.sitelistForLegend().map((site) => site.name)),
    );

    const legends = useMemo(
        () =>
            chosenSites.sitelistForLegend().map((site) => ({
                name: site.name,
                color: site.color,
                visible: visibleLegends.has(site.name),
                data: data?.[site.name]?.totalVisits ? minAbbrNumberFilter()(data[site.name].totalVisits) : "N/A",
            })),
        [data, visibleLegends],
    );

    const handleLegendClick = useCallback((filter: ILegendItem) => {
        setVisibleLegends((prev) => {
            const next = new Set(prev);
            next.has(filter.name) ? next.delete(filter.name) : next.add(filter.name);
            return next;
        });

        allTrackers.dispatchTrackEvent(
            "Click Filter",
            filter.visible ? "remove" : "click",
            `Bar Chart/Traffic Sources Overview/${filter.name}`,
        );
    }, []);

    const filteredSeries = useMemo(
        () => series.filter((item) => visibleLegends.has(item.name)),
        [series, visibleLegends],
    );
    const shouldShowLegends = isCompareMode() && legends.length > 0;
    return (
        <ChartContainer>
            <ChartWrapper>
                {totalVisits ? (
                    <TotalSingleWrapper>
                        <TotalTrafficTitle>
                            {translate("mmx.channelsoverview.bar.chart.total.traffic.title.total")}
                        </TotalTrafficTitle>
                        <TotalTrafficNumber>{minAbbrNumberFilter()(totalVisits)}</TotalTrafficNumber>
                    </TotalSingleWrapper>
                ) : null}
                <ChannelsOverviewBarChart
                    data={filteredSeries}
                    config={config}
                    dataAutomation="ChannelsOverviewBarChart"
                />
            </ChartWrapper>
            {shouldShowLegends ? (
                <LegendsContainer>
                    <ChannelsOverviewBarChartLegend
                        legendItems={legends}
                        onLegendsChange={handleLegendClick}
                        titles={[
                            translate("common.domain"),
                            translate("mmx.channelsoverview.channels.legend.values.title"),
                        ]}
                    />
                </LegendsContainer>
            ) : null}
        </ChartContainer>
    );
};

export default BetaChannelsChart;
